// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { memo, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  mediaMax,
  fontSize,
  fontWeight,
  spacing,
  isMediaMax,
  zIndex,
} from 'presentations/views/styles';
import { gray01, gray04, black } from 'presentations/views/styles/color';
import Link from 'presentations/views/components/atoms/Link';
import AnonymousUserMenuList from 'presentations/views/components/molecules/AnonymousUserMenuList';
import Logo from 'presentations/views/components/atoms/Logo';
import {
  changelogListPath,
  changelogPath,
  homePath,
  pricingPath,
} from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';
import { isInElectron } from 'presentations/views/helpers/electron';
import {
  developerCenterApiReferencePath,
  developerCenterPath,
  developerPath,
} from 'presentations/routes/path/developer';

interface Props {
  className?: string;
  viewer: ViewerItem | null;
  isLoading?: boolean;
}

const Component = memo((props: Props) => {
  const t = translateInComponent(translations);
  const [isElectron, setIsElectron] = useState<boolean>(false);

  useEffect(() => {
    setIsElectron(isInElectron());
  }, []);

  const isMobile = isMediaMax('tablet');
  const [isHomeDrawerOpen, setIsHomeDrawerOpen] = useState<boolean>(false);

  const clickHomeDrawer = () => {
    setIsHomeDrawerOpen(!isHomeDrawerOpen);
  };

  return (
    <Container className={props.className} isElectron={isElectron}>
      <LeftBox isElectron={isElectron}>
        {!isMobile && (
          <Link href={homePath()}>
            <ServiceImage width={130} height={34} />
          </Link>
        )}
        {isMobile && (
          <DashboardBackMenuListItem>
            <AnonymousUserMenuList
              currentUrl={developerPath()}
              viewer={props.viewer}
              isDrawerOpen={isHomeDrawerOpen}
              onClick={clickHomeDrawer}
            />
          </DashboardBackMenuListItem>
        )}
        {!isMobile && (
          <Navigation>
            <NavigationItem>
              <NavigationLink href={developerCenterPath()}>
                {t('Document')}
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink href={developerCenterApiReferencePath()}>
                {t('API Reference')}
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink href={changelogListPath()}>
                {t('Changelog')}
              </NavigationLink>
            </NavigationItem>
            <NavigationItem>
              <NavigationLink href={pricingPath()}>
                {t('Pricing')}
              </NavigationLink>
            </NavigationItem>
          </Navigation>
        )}
      </LeftBox>
      <BrandContainer>
        {isMobile && (
          <Link href={homePath()}>
            <ServiceImage width={130} height={34} />
          </Link>
        )}
      </BrandContainer>
      <RightBox></RightBox>
    </Container>
  );
});

const Container = styled.div<{ isElectron: boolean }>`
  display: flex;
  align-items: center;
  height: 80px;
  position: sticky;
  top: 0;
  background-color: transparent;
  transition: all 300ms ease-out;
  -webkit-app-region: drag;
  z-index: ${zIndex(100)};
  ${mediaMax.tablet} {
    height: 54px;
    justify-content: space-between;
  }
  ${({ isElectron }) =>
    isElectron
      ? css`
          height: 42px;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: ${zIndex(1400)};
        `
      : ''}
`;
const LeftBox = styled.div<{
  isElectron: boolean;
}>`
  margin-right: auto;
  margin-left: ${({ isElectron }) => (isElectron ? spacing(40) : spacing(24))};
  display: flex;
  align-items: center;
  z-index: 1;
  ${mediaMax.tablet} {
    margin-left: ${spacing(16)};
    min-width: 80px;
    margin-right: initial;
  }
  ${mediaMax.xs} {
    width: 34px;
    overflow: hidden;
  }
`;
const Navigation = styled.ul`
  margin-left: ${spacing(48)};
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const NavigationItem = styled.li`
  margin-right: ${spacing(24)};
  :last-of-type {
    margin-right: 0;
  }
`;
const NavigationLink = styled(Link)`
  color: ${black};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(14)};
  letter-spacing: 0.01em;
  display: flex;
  align-items: center;
`;
const ServiceImage = styled(Logo)`
  width: 130px;
  height: auto;

  ${mediaMax.tablet} {
    width: 89px;
  }
`;
const RightBox = styled.div`
  margin-right: ${spacing(24)};
  z-index: 1;
  ${mediaMax.tablet} {
    margin-right: ${spacing(8)};
    min-width: 80px;
  }
  ${mediaMax.xs} {
    margin-right: ${spacing(3)};
  }
`;
const DashboardBackMenuListItem = styled.div<{ isElectron?: boolean }>`
  margin-right: ${spacing(24)};
  padding-right: ${spacing(24)};
  border-right: 1px solid ${gray04};
  /* color: ${gray01}; */
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    margin-right: 0;
    padding-right: 0;
    border-right: none;

    ${({ isElectron }) =>
      isElectron &&
      css`
        margin-left: ${spacing(64)};
      `}
  }
`;

const BrandContainer = styled.div`
  flex: 1;
  text-align: center;
`;
export default Component;
